// Report something from the dashboard (contract createClaim). Capture only:
// what happened, when, where, what was taken, photos. Nothing here talks
// about whether it's covered or what will be paid.
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, X } from 'lucide-react';
import { ok } from '@cover/core/api/client.ts';
import type { Claim, Document } from '@cover/core/api/types.ts';
import { useBrand, whatsappLink } from '@cover/core/brand.tsx';
import { money } from '@cover/core/format.ts';
import { Button } from '@cover/core/ui/Button.tsx';
import { Card, CardBody } from '@cover/core/ui/Card.tsx';
import { Field, Input, Textarea } from '@cover/core/ui/Form.tsx';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { api, keys, useAssets, usePolicies } from '../api.ts';
import { UploadButton } from '../components/Upload.tsx';
import { PageTitle } from '../Layout.tsx';
import { useBusinessId } from '../session.tsx';

const TYPES: [Claim['type'], string, string][] = [
  ['theft', 'Something was stolen', 'Tools, equipment or anything else taken'],
  ['damage', 'Something was damaged', 'Your tools, van contents or equipment'],
  ['property_damage_third_party', 'I damaged someone else’s property', 'For example at a customer’s home'],
  ['injury', 'Someone was hurt', 'You, someone working for you, or someone else'],
  ['other', 'Something else', 'Tell us in your own words'],
];

function today() {
  return new Date().toISOString().slice(0, 10);
}

export function NewClaimPage() {
  const b = useBusinessId();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const brand = useBrand();
  const assets = useAssets();
  const policies = usePolicies();
  const wa = whatsappLink(brand, 'I need to make a claim');

  const [type, setType] = useState<Claim['type'] | null>(null);
  const [day, setDay] = useState(today());
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [policeRef, setPoliceRef] = useState('');
  const [injuries, setInjuries] = useState(false);
  const [vulnerable, setVulnerable] = useState(false);
  const [stolen, setStolen] = useState<string[]>([]);
  const [docs, setDocs] = useState<Document[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: () =>
      ok(
        api.POST('/businesses/{businessId}/claims', {
          params: { path: { businessId: b } },
          body: {
            type: type!,
            occurredAt: new Date(`${day}T${time || '12:00'}`).toISOString(),
            location: location.trim() || null,
            description: description.trim(),
            policeRef: policeRef.trim() || null,
            injuries: injuries || type === 'injury',
            vulnerable,
            policyNumber: policies.data?.find((p) => p.status === 'active')?.policyNumber ?? null,
            stolenAssetIds: type === 'theft' ? stolen : [],
            documentIds: docs.map((d) => d.id),
            submit: true,
          },
        }),
      ),
    onSuccess: async (claim) => {
      await Promise.all([qc.invalidateQueries({ queryKey: keys.claims(b) }), qc.invalidateQueries({ queryKey: keys.assets(b) }), qc.invalidateQueries({ queryKey: keys.documents(b) })]);
      toast.ok(`Thanks. Your claim reference is ${claim.reference}.`);
      navigate(`/claims/${claim.id}`);
    },
    onError: (e) => toast.fail(e),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!type) errs.type = 'Choose what happened.';
    if (!day) errs.day = 'Tell us roughly when it happened.';
    else if (day > today()) errs.day = 'That date is in the future.';
    if (description.trim().length < 10) errs.description = 'Tell us a bit more about what happened.';
    setErrors(errs);
    if (Object.keys(errs).length) {
      // Field sets its own ids, so find the first invalid control once the errors render.
      const form = e.currentTarget as HTMLFormElement;
      setTimeout(() => (errs.type ? document.getElementById('f-type') : form.querySelector<HTMLElement>('[aria-invalid="true"]'))?.focus());
      return;
    }
    create.mutate();
  }

  const active = (assets.data ?? []).filter((a) => a.status === 'active');

  return (
    <>
      <Link to="/claims" className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm">
        <ArrowLeft className="h-4 w-4" aria-hidden /> All claims
      </Link>
      <PageTitle lead="Tell us what happened. You can add more later, and we’ll message you about what happens next.">Report something</PageTitle>

      <form className="space-y-5" onSubmit={submit} noValidate>
        <Card>
          <CardBody>
            <fieldset aria-describedby={errors.type ? 'err-type' : undefined}>
              <legend className="mb-3 font-semibold">What happened?</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {TYPES.map(([value, label, hint], i) => (
                  <label
                    key={value}
                    className="flex min-h-14 cursor-pointer items-start gap-3 rounded-md border border-line px-3 py-3 has-[:checked]:border-brand has-[:checked]:bg-brand/5 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand"
                  >
                    <input id={i === 0 ? 'f-type' : undefined} type="radio" name="type" value={value} checked={type === value} onChange={() => setType(value)} className="mt-1 h-4 w-4 accent-[var(--brand)]" />
                    <span>
                      <span className="block font-medium">{label}</span>
                      <span className="block text-sm text-mid">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
              {errors.type && (
                <p id="err-type" className="mt-2 text-sm font-medium text-bad-fg">
                  {errors.type}
                </p>
              )}
            </fieldset>
          </CardBody>
        </Card>

        {type && (
          <Card>
            <CardBody className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="When did it happen?" error={errors.day}>
                  <Input type="date" max={today()} value={day} onChange={(e) => setDay(e.target.value)} />
                </Field>
                <Field label="Roughly what time?" hint="If you know">
                  <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                </Field>
              </div>
              <Field label="Where?" hint="An address, postcode or description, like “outside a job in Leeds”">
                <Input value={location} maxLength={200} onChange={(e) => setLocation(e.target.value)} autoComplete="off" />
              </Field>
              <Field label="What happened?" hint="In your own words. Include anything you think we should know." error={errors.description}>
                <Textarea value={description} maxLength={4000} rows={5} onChange={(e) => setDescription(e.target.value)} />
              </Field>
              {(type === 'theft' || type === 'damage') && (
                <Field label="Police crime reference" hint="If you’ve reported it. You can add it later.">
                  <Input value={policeRef} maxLength={40} onChange={(e) => setPoliceRef(e.target.value)} autoComplete="off" />
                </Field>
              )}
            </CardBody>
          </Card>
        )}

        {type === 'theft' && (
          <Card>
            <CardBody>
              <fieldset>
                <legend className="font-semibold">What was taken?</legend>
                <p className="mb-3 text-sm text-mid">Tick anything from your equipment list. Missing something? Describe it above.</p>
                {active.length ? (
                  <ul className="max-h-80 divide-y divide-line overflow-y-auto rounded-md border border-line">
                    {active.map((a) => (
                      <li key={a.id}>
                        <label className="flex min-h-12 cursor-pointer items-center gap-3 px-3 py-2">
                          <input
                            type="checkbox"
                            className="h-5 w-5 accent-[var(--brand)]"
                            checked={stolen.includes(a.id)}
                            onChange={(e) => setStolen((s) => (e.target.checked ? [...s, a.id] : s.filter((x) => x !== a.id)))}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block font-medium">{a.name}</span>
                            {(a.make || a.serial) && <span className="block text-sm text-mid">{[a.make, a.model, a.serial && `Serial ${a.serial}`].filter(Boolean).join(' · ')}</span>}
                          </span>
                          {a.valuePence != null && <span className="text-sm text-mid">{money(a.valuePence)}</span>}
                        </label>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-mid">Your equipment list is empty, so describe what was taken above.</p>
                )}
                {stolen.length > 0 && (
                  <p className="mt-2 text-sm" aria-live="polite">
                    {stolen.length} ticked, {money(active.filter((a) => stolen.includes(a.id)).reduce((s, a) => s + (a.valuePence ?? 0), 0))} in total
                  </p>
                )}
              </fieldset>
            </CardBody>
          </Card>
        )}

        {type && (
          <Card>
            <CardBody className="space-y-4">
              <div>
                <h2 className="font-semibold">Photos and documents</h2>
                <p className="text-sm text-mid">Photos of the damage, a police report, receipts. Optional now; you can add them later.</p>
              </div>
              {docs.length > 0 && (
                <ul className="space-y-2">
                  {docs.map((d) => (
                    <li key={d.id} className="flex items-center gap-2 rounded-md bg-canvas px-3 py-2 text-sm">
                      <FileText className="h-4 w-4 shrink-0 text-mid" aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{d.filename}</span>
                      <button type="button" className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-white" aria-label={`Don’t include ${d.filename}`} onClick={() => setDocs((x) => x.filter((y) => y.id !== d.id))}>
                        <X className="h-4 w-4" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2">
                <UploadButton label="Take a photo" accept="image/*" capture hint={type === 'theft' ? undefined : 'damage_photo'} onUploaded={(d) => setDocs((x) => [...x, d])} />
                <UploadButton label="Add a file" onUploaded={(d) => setDocs((x) => [...x, d])} />
              </div>
            </CardBody>
          </Card>
        )}

        {type && (
          <Card>
            <CardBody className="space-y-3">
              {type !== 'injury' && (
                <label className="flex min-h-11 cursor-pointer items-start gap-3">
                  <input type="checkbox" className="mt-1 h-5 w-5 accent-[var(--brand)]" checked={injuries} onChange={(e) => setInjuries(e.target.checked)} />
                  <span>Someone was hurt</span>
                </label>
              )}
              <label className="flex min-h-11 cursor-pointer items-start gap-3">
                <input type="checkbox" className="mt-1 h-5 w-5 accent-[var(--brand)]" checked={vulnerable} onChange={(e) => setVulnerable(e.target.checked)} />
                <span>
                  I need some extra help with this
                  <span className="block text-sm text-mid">For example if it’s been upsetting, or you’d find it easier to talk to a person.</span>
                </span>
              </label>
              {(injuries || vulnerable || type === 'injury') && <p className="rounded-md bg-info-bg/60 p-3 text-sm">A member of our team will pick this up as a priority.</p>}
            </CardBody>
          </Card>
        )}

        <div className="space-y-3">
          <Button type="submit" size="full" busy={create.isPending} disabled={!type}>
            Send to our claims team
          </Button>
          {wa && (
            <p className="text-center text-sm text-mid">
              Rather talk it through? <a href={wa}>Message us in WhatsApp</a>.
            </p>
          )}
        </div>
      </form>
    </>
  );
}
